import type MonitoredEvent from '@poseidon/types/pg-gen/monitoredEvent';
import type { SvrWThrottling } from '@poseidon/types/svr/svrUserTeam.type';

/** The outbound send channels the sidebar Today meter reports on, in display order. Social
 *  activity (visit / follow / likes) and calls are deliberately excluded — the
 *  question the strip answers is "did outreach go out today, and how much". */
export const SEND_EVENTS = ['connect', 'message', 'inmail', 'email'] as const;
export type SendEvent = (typeof SEND_EVENTS)[number];

export const SEND_EVENT_LABELS: Record<SendEvent, string> = {
  connect: 'Connections',
  message: 'Messages',
  inmail: 'InMails',
  email: 'Emails',
};

/** One send of each channel, for a row that names what went out. */
export const SEND_EVENT_LABEL_SINGULAR: Record<SendEvent, string> = {
  connect: 'Connection request',
  message: 'Message',
  inmail: 'InMail',
  email: 'Email',
};

export function isSendEvent(value: string): value is SendEvent {
  return SEND_EVENTS.some((event) => event === value);
}

export type SendsTodayEntry = {
  event: SendEvent;
  /** Sends counted today across the selected users. */
  sent: number;
  /** Sum of the selected users' daily caps for this channel. */
  cap: number;
  /** Failed attempts today — they consume daily budget too. */
  errors: number;
};

type UserWithThrottling = SvrWThrottling & { userId: string };

/**
 * Aggregate today's per-channel send counts from the throttling data the
 * team-users payload already carries. `userId` scopes to one rep; 'team',
 * undefined, or an unknown id means the whole team. Channels with no budget
 * AND no sends anywhere are dropped (channel not in use for this team).
 */
export function aggregateSendsToday(users: UserWithThrottling[], userId?: string): SendsTodayEntry[] {
  const scoped = userId != null && userId !== 'team' ? users.filter((u) => u.userId === userId) : users;

  const entries = SEND_EVENTS.map((event) => {
    let sent = 0;
    let cap = 0;
    let errors = 0;
    for (const u of scoped) {
      if (u.throttling == null) continue;
      sent += u.throttling.stepDailyCounts[event as MonitoredEvent] ?? 0;
      cap += u.throttling.stepLimits[event as MonitoredEvent] ?? 0;
      errors += u.throttling.stepDailyErrors[event as MonitoredEvent] ?? 0;
    }
    return { event, sent, cap, errors };
  });

  return entries.filter((e) => e.cap > 0 || e.sent > 0);
}

export type SendsTodayTone = 'sending' | 'idle' | 'capped';

/** Tone for a chip: green when sends went out, amber when today's budget is
 *  exhausted (sends + errors reached the cap), muted otherwise. */
export function sendsTodayTone(entry: SendsTodayEntry): SendsTodayTone {
  if (entry.cap > 0 && entry.sent + entry.errors >= entry.cap) return 'capped';
  if (entry.sent > 0) return 'sending';
  return 'idle';
}

export type TodayMeterState = 'normal' | 'capped' | 'quiet';

/** After this local hour on a weekday, zero sends with people enrolled is worth flagging. */
export const QUIET_AFTER_HOUR = 13;

/**
 * The sidebar meter stays muted unless something is off:
 * - `capped`: a channel has used its whole daily budget (sends + failures).
 * - `quiet`: it's a weekday afternoon in the rep's timezone, people are enrolled, and nothing
 *   has gone out on any channel. At 7am zero is normal; at 3pm it usually means outreach is stuck.
 */
export function todayMeterState(
  entries: SendsTodayEntry[],
  opts: { hasActiveEnrollments: boolean; timezoneName: string | null; now: Date },
): TodayMeterState {
  if (entries.some((e) => sendsTodayTone(e) === 'capped')) return 'capped';
  const totalSent = entries.reduce((sum, e) => sum + e.sent, 0);
  if (totalSent > 0 || !opts.hasActiveEnrollments || entries.length === 0) return 'normal';
  const { weekday, hour } = localWeekdayHour(opts.now, opts.timezoneName);
  const isWeekday = weekday !== 'Sat' && weekday !== 'Sun';
  return isWeekday && hour >= QUIET_AFTER_HOUR ? 'quiet' : 'normal';
}

function localWeekdayHour(now: Date, timezoneName: string | null): { weekday: string; hour: number } {
  const format = (timeZone: string | undefined) =>
    new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short', hour: 'numeric', hourCycle: 'h23' }).formatToParts(
      now,
    );
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = format(timezoneName ?? undefined);
  } catch {
    parts = format(undefined); // unknown zone name: fall back to the browser's
  }
  return {
    weekday: parts.find((p) => p.type === 'weekday')?.value ?? '',
    hour: Number(parts.find((p) => p.type === 'hour')?.value ?? 0),
  };
}
